import React from 'react';
import PropTypes from 'prop-types';
import { rgba } from 'polished';

import { withStyles } from '../tools/withStyles';
import { withSounds } from '../tools/withSounds';
import { Link } from '../components/Link';
import { Main } from '../components/Main';
import { Text } from '../components/Text';
import { Secuence } from '../components/Secuence';
import { GithubContributions } from '../components/GithubContributions';

const styles = theme => ({
  root: {},
  experience: {
    marginTop: 36
  },
  activity: {
    marginTop: 36
  },
  contact: {
    marginTop: 36
  },
  sectionTitle: {
    marginBottom: 20,
    fontSize: 22
  },
  timeline: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 18,
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
    }
  },
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 18,
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
    }
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: 186,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.24),
    padding: [24, 18, 22],
    textAlign: 'center',
    backgroundColor: rgba(theme.color.background.dark, 0.28),
    transition: [
      `border-color ${theme.animation.time}ms ease-out`,
      `background-color ${theme.animation.time}ms ease-out`,
      `box-shadow ${theme.animation.time}ms ease-out`,
      `transform ${theme.animation.time}ms ease-out`
    ].join(','),

    '&:hover': {
      borderColor: rgba(theme.color.secondary.main, 0.62),
      backgroundColor: rgba(theme.color.secondary.main, 0.045),
      boxShadow: `0 0 20px ${rgba(theme.color.secondary.main, 0.12)}`,
      transform: 'translateY(-2px)'
    }
  },
  contactCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 140,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.24),
    padding: [24, 18, 22],
    textAlign: 'center',
    backgroundColor: rgba(theme.color.background.dark, 0.28),
    textDecoration: 'none',
    transition: [
      `border-color ${theme.animation.time}ms ease-out`,
      `background-color ${theme.animation.time}ms ease-out`,
      `box-shadow ${theme.animation.time}ms ease-out`,
      `transform ${theme.animation.time}ms ease-out`
    ].join(','),

    '&:hover, &:focus': {
      borderColor: rgba(theme.color.secondary.main, 0.62),
      backgroundColor: rgba(theme.color.secondary.main, 0.045),
      boxShadow: `0 0 20px ${rgba(theme.color.secondary.main, 0.12)}`,
      transform: 'translateY(-2px)'
    }
  },
  contactTitle: {
    margin: [0, 0, 8],
    color: theme.color.primary.main,
    fontSize: 18,
    lineHeight: 1.2,
    fontFamily: theme.typography.primary,
    textTransform: 'uppercase',
    textShadow: `0 0 5px ${theme.color.secondary.main}`
  },
  contactValue: {
    margin: 0,
    color: theme.color.secondary.main,
    fontSize: 13,
    lineHeight: 1.5,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    fontFamily: theme.typography.secondary
  },
  mark: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 72,
    height: 54,
    marginBottom: 16
  },
  tux: {
    display: 'block',
    width: 48,
    height: 48,
    maxWidth: 'none',
    minHeight: 'auto',
    margin: 0,
    border: 'none',
    padding: 0
  },
  logoFrame: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 134,
    height: 38,
    marginBottom: 16,
    padding: 0,
    backgroundColor: 'transparent'
  },
  logo: {
    display: 'block',
    width: '100%',
    height: 'auto',
    maxWidth: '100%',
    minHeight: 'auto',
    margin: 0,
    border: 'none',
    padding: 0
  },
  technooneLogo: {
    filter: [
      'brightness(0)',
      'invert(1)',
      'drop-shadow(0 0 5px ' + rgba(theme.color.secondary.main, 0.42) + ')'
    ].join(' ')
  },
  company: {
    margin: [0, 0, 8],
    color: theme.color.primary.main,
    fontSize: 18,
    lineHeight: 1.2,
    fontFamily: theme.typography.primary,
    textTransform: 'uppercase',
    textShadow: `0 0 5px ${theme.color.secondary.main}`
  },
  role: {
    margin: [0, 0, 14],
    color: theme.color.text.main,
    fontSize: 14,
    lineHeight: 1.45,
    letterSpacing: 0,
    textTransform: 'uppercase'
  },
  period: {
    margin: 'auto 0 0',
    color: theme.color.secondary.main,
    fontSize: 12,
    lineHeight: 1.4,
    letterSpacing: 0,
    textTransform: 'uppercase'
  }
});

const ABOUT_TEXT_DURATION = 580;
const ABOUT_COPY = 'I am a software developer interested in technology and cybersecurity, focused on continuous self-improvement. I work on cybersecurity, penetration testing, artificial intelligence, and modern software technologies, and I care about learning new technologies quickly to build practical, innovative solutions.';

const playSound = (sounds, name) => {
  const sound = sounds && sounds[name];

  if (sound) {
    sound.play();
  }
};

const SlowText = ({ children }) => (
  <Text
    stableTime
    typingDuration={ABOUT_TEXT_DURATION}
    animation={{ duration: ABOUT_TEXT_DURATION }}
  >
    {children}
  </Text>
);

SlowText.propTypes = {
  children: PropTypes.string.isRequired
};

class Hakkimda extends React.Component {
  static propTypes = {
    classes: PropTypes.object,
    sounds: PropTypes.object
  };

  render () {
    const { classes, sounds } = this.props;

    return (
      <Main className={classes.root}>
        <article>
          <Secuence stagger={80}>
            <header>
              <h1><SlowText>About</SlowText></h1>
            </header>
            <p>
              <SlowText>{ABOUT_COPY}</SlowText>
            </p>
            <section className={classes.experience} aria-labelledby='experience-title'>
              <h2 id='experience-title' className={classes.sectionTitle}>
                <SlowText>Experience</SlowText>
              </h2>
              <div className={classes.timeline}>
                <article
                  className={classes.item}
                  onMouseEnter={() => playSound(sounds, 'hover')}
                >
                  <span className={classes.mark} aria-hidden='true'>
                    <img
                      className={classes.tux}
                      src='/images/tux.svg'
                      alt=''
                    />
                  </span>
                  <h3 className={classes.company}><SlowText>Linux</SlowText></h3>
                  <p className={classes.role}><SlowText>Linux Kernel Contributor</SlowText></p>
                  <p className={classes.period}><SlowText>March 2026 - Present</SlowText></p>
                </article>
                <article
                  className={classes.item}
                  onMouseEnter={() => playSound(sounds, 'hover')}
                >
                  <span className={classes.logoFrame}>
                    <img
                      className={`${classes.logo} ${classes.technooneLogo}`}
                      src='/images/technoone.svg'
                      alt='TechnoOne'
                    />
                  </span>
                  <h3 className={classes.company}><SlowText>TechnoOne</SlowText></h3>
                  <p className={classes.role}><SlowText>Software Intern</SlowText></p>
                  <p className={classes.period}><SlowText>July 2026 - August 2026</SlowText></p>
                </article>
              </div>
            </section>
            <section className={classes.activity} aria-labelledby='activity-title'>
              <h2 id='activity-title' className={classes.sectionTitle}>
                <SlowText>GitHub Activity</SlowText>
              </h2>
              <GithubContributions username='melihemik' />
            </section>
            <section className={classes.contact} aria-labelledby='contact-title'>
              <h2 id='contact-title' className={classes.sectionTitle}>
                <SlowText>Contact & Security</SlowText>
              </h2>
              <div className={classes.contactGrid}>
                <Link
                  className={classes.contactCard}
                  href='mailto:melihemik@noirlang.tr'
                  target='email'
                  onMouseEnter={() => playSound(sounds, 'hover')}
                >
                  <h3 className={classes.contactTitle}><SlowText>Email</SlowText></h3>
                  <p className={classes.contactValue}><SlowText>melihemik@noirlang.tr</SlowText></p>
                </Link>
                <Link
                  className={classes.contactCard}
                  href='https://keys.openpgp.org/search?q=melihemik@noirlang.tr'
                  target='pgp'
                  rel='noreferrer'
                  onMouseEnter={() => playSound(sounds, 'hover')}
                >
                  <h3 className={classes.contactTitle}><SlowText>PGP Key</SlowText></h3>
                  <p className={classes.contactValue}><SlowText>DB00 D360 9762 7EDC AA5F 759C CF4D 46CF 340D 424F</SlowText></p>
                </Link>
              </div>
            </section>
          </Secuence>
        </article>
      </Main>
    );
  }
}

export default withStyles(styles)(withSounds()(Hakkimda));
